import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { Card, Flex, Separator, Text } from '@radix-ui/themes'
import {
  type ActionFunctionArgs,
  data,
  Form,
  Link,
  type LoaderFunctionArgs,
  type MetaFunction,
  useActionData,
  useLoaderData,
  useSearchParams,
} from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/ui/error-boundary.js'
import { ErrorList } from '#app/components/ui/forms/form-error-list.js'
import { CheckboxField, Field } from '#app/components/ui/forms/forms.js'
import {
  DexIconExternalLink,
  DexIconLock,
  DexIconUser,
} from '#app/components/ui/icons/dex-icons.js'
import { StatusButton } from '#app/components/ui/other/status-button.js'
import {
  PasswordSchema,
  UsernameSchema,
} from '#app/data/validation/user-validation.js'
import { AUTH_METHOD, login, requireAnonymous } from '#app/utils/auth.server.ts'
import {
  isSelfHosted,
  isTuniAuth,
  isTuniAuthOptional,
} from '#app/utils/env.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { handleNewSession, handleTuniLogin } from './login.server.ts'

const OPTIONAL_TUNI_LOGIN_PATH = '/auth/tuni-login'

const LoginFormSchema = z.object({
  username: UsernameSchema,
  password: PasswordSchema,
  redirectTo: z.string().optional(),
  remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
  if (isTuniAuth()) {
    return handleTuniLogin(request)
  } else {
    await requireAnonymous(request)

    if (isTuniAuthOptional()) {
      return data({ authMode: 'optional' as const, selfHosted: isSelfHosted() })
    }

    return data({ authMode: 'password' as const, selfHosted: isSelfHosted() })
  }
}

export async function action({ request }: ActionFunctionArgs) {
  await requireAnonymous(request)
  const formData = await request.formData()
  checkHoneypot(formData)
  const submission = await parseWithZod(formData, {
    schema: intent =>
      LoginFormSchema.transform(async (data, ctx) => {
        if (intent !== null) return { ...data, session: null }

        const session = await login(data)
        if (!session) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid username or password',
          })
          return z.NEVER
        }

        return { ...data, session }
      }),
    async: true,
  })

  if (submission.status !== 'success' || !submission.value.session) {
    return data(
      { result: submission.reply({ hideFields: ['password'] }) },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const { session, remember, redirectTo } = submission.value

  return handleNewSession({
    request,
    session,
    authMethod: AUTH_METHOD.PASSWORD,
    remember: remember ?? false,
    redirectTo,
  })
}

export default function LoginPage() {
  const loaderData = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const isPending = useIsPending()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo')
  const isOptionalMode = loaderData.authMode === 'optional'
  const selfHosted = loaderData.selfHosted
  const tuniLoginUrl = redirectTo
    ? `${OPTIONAL_TUNI_LOGIN_PATH}?${new URLSearchParams({ redirectTo }).toString()}`
    : OPTIONAL_TUNI_LOGIN_PATH

  const [form, fields] = useForm({
    id: 'login-form',
    constraint: getZodConstraint(LoginFormSchema),
    defaultValue: { redirectTo },
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: LoginFormSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  return (
    <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col px-6 pb-20 pt-12 sm:px-8 lg:px-12">
      <div className="mx-auto my-auto w-full max-w-5xl">
        <div
          className={
            isOptionalMode
              ? 'mb-10 flex flex-col gap-3 text-center lg:text-left'
              : 'mb-10 flex flex-col gap-3 text-center'
          }
        >
          <Text as="p" size="2" weight="medium" color="gray">
            Design Explorer
          </Text>
          <h1 className="text-h1">Sign in to continue</h1>
          <p className="text-body-md text-muted-foreground">
            {isOptionalMode
              ? 'Choose your sign-in method for this staging environment.'
              : 'Use your DEx account credentials to access your workspace.'}
          </p>
        </div>

        <div
          className={
            isOptionalMode ? 'grid gap-6 lg:grid-cols-[0.9fr_1.1fr]' : ''
          }
        >
          {isOptionalMode ? (
            <Card className="border-border/60 p-6 shadow-sm">
              <Flex direction="column" gap="4">
                <div className="flex items-start gap-4">
                  <div className="rounded-full bg-primary/10 p-3">
                    <DexIconLock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <Text as="div" size="5" weight="bold">
                      TUNI Single Sign-On
                    </Text>
                    <Text as="p" size="2" color="gray" className="mt-2">
                      Continue with Tampere University authentication when you
                      want to test the SSO flow.
                    </Text>
                  </div>
                </div>

                <Separator size="4" />

                <Flex direction="column" gap="3">
                  <Link to={tuniLoginUrl} className="no-underline">
                    <StatusButton
                      className="w-full"
                      status="idle"
                      type="button"
                    >
                      Continue with TUNI SSO
                    </StatusButton>
                  </Link>

                  <Text as="p" size="1" color="gray">
                    Use this when you need real Shibboleth headers and TUNI user
                    provisioning behavior.
                  </Text>
                </Flex>
              </Flex>
            </Card>
          ) : null}

          <Card
            className={
              isOptionalMode
                ? 'border-border/60 p-6 shadow-sm'
                : 'mx-auto w-full max-w-2xl border-border/60 p-6 shadow-sm'
            }
          >
            <Flex direction="column" gap="4">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/10 p-3">
                  <DexIconUser className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <Text as="div" size="5" weight="bold">
                    {isOptionalMode ? 'Password sign-in' : 'Sign in'}
                  </Text>
                  <Text as="p" size="2" color="gray" className="mt-2">
                    {isOptionalMode
                      ? 'Use a local DEx account to test invitations, password management, and non-SSO flows.'
                      : 'Enter your account credentials below.'}
                  </Text>
                </div>
              </div>

              <Separator size="4" />

              <Form method="POST" {...getFormProps(form)}>
                <HoneypotInputs />
                <Field
                  labelProps={{ children: 'Username' }}
                  inputProps={{
                    ...getInputProps(fields.username, { type: 'text' }),
                    autoFocus: true,
                    className: 'lowercase',
                    autoComplete: 'username',
                  }}
                  errors={fields.username.errors}
                />

                <Field
                  labelProps={{ children: 'Password' }}
                  inputProps={{
                    ...getInputProps(fields.password, {
                      type: 'password',
                    }),
                    autoComplete: 'current-password',
                  }}
                  errors={fields.password.errors}
                />

                <div className="flex justify-between">
                  <CheckboxField
                    labelProps={{
                      htmlFor: fields.remember.id,
                      children: 'Remember me',
                    }}
                    buttonProps={getInputProps(fields.remember, {
                      type: 'checkbox',
                    })}
                    errors={fields.remember.errors}
                  />
                </div>

                <input
                  {...getInputProps(fields.redirectTo, { type: 'hidden' })}
                />
                <ErrorList errors={form.errors} id={form.errorId} />

                <div className="flex items-center justify-between gap-6 pt-3">
                  <StatusButton
                    className="w-full"
                    status={isPending ? 'pending' : (form.status ?? 'idle')}
                    type="submit"
                    disabled={isPending}
                  >
                    Log in
                  </StatusButton>
                </div>
              </Form>

              {!selfHosted && (
                <Text as="p" size="1" color="gray" align="center">
                  By signing in you agree to our{' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    className="inline-flex items-center gap-0.5 underline"
                  >
                    privacy policy
                    <DexIconExternalLink className="h-3 w-3" />
                  </Link>{' '}
                  and{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    className="inline-flex items-center gap-0.5 underline"
                  >
                    terms of service
                    <DexIconExternalLink className="h-3 w-3" />
                  </Link>
                  .
                </Text>
              )}
            </Flex>
          </Card>
        </div>
      </div>
    </div>
  )
}

export const meta: MetaFunction = () => {
  return [{ title: 'Sign in to DEx' }]
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
